import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { CheckCircle, Calendar, Users, Heart, Download, Mail, MapPin, Gift, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import SubscribeModal from '@/components/subscriptions/SubscribeModal';

const Community = () => {
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeSource, setSubscribeSource] = useState('community_plugin');

  const openSubscribe = (source) => {
    setSubscribeSource(source);
    setSubscribeOpen(true);
  };

  const events = [
    {
      title: 'Business Networking Breakfasts',
      description: 'Real conversations, zero sales pitches. Join us for coffee, connection, and practical discussions about business, growth, and money. You will leave with new ideas, new contacts, and a little more clarity.',
      icon: Users
    },
    {
      title: 'Free Coffee Fridays',
      description: 'Drop in, grab a cup, say hi. No appointment required. This is a relaxed way to meet our team, ask a quick question, or just sit in a space where people are talking openly about money instead of avoiding it.',
      icon: Mail
    },
    {
      title: 'Kids & Families Events (If You Teach Me)',
      description: 'Our charitable initiative, If You Teach Me, focuses on financial literacy for kids and families. Think Toys for Tots drives, Santa visits, and simple money lessons that build confidence early.',
      icon: Heart
    },
    {
      title: 'Online Workshops & Webcasts',
      description: 'Can\'t make it in person? Join from anywhere. We host short, focused sessions on topics like "What to Do When You Get an IRS Letter" or "Tax Changes You Should Actually Care About."',
      icon: BookOpen
    }
  ];

  const initiatives = [
    {
      title: 'If You Teach Me (IYTM)',
      description: 'We believe financial confidence is one of the greatest gifts you can give a child.',
      features: [
        'Financial literacy experiences for kids and families',
        'Seasonal events like Toys for Tots drives and Santa visits',
        'A focus on leading by example—showing kids how adults handle money with honesty and calm'
      ],
      note: 'You will see IYTM highlighted at many of our community events. We would love for you to be part of it.'
    },
    {
      title: 'Let\'s Talk Growth – Supporting Local Businesses',
      description: 'Through our Let\'s Talk Growth platform, we help business owners understand what their company is worth and what\'s driving (or hurting) that value.',
      features: [
        'Clear, accessible valuation reports',
        'Growth-focused advisory for owners who want to build a stronger, more resilient business',
        'Opportunities throughout the year for local owners to learn and connect'
      ]
    },
    {
      title: 'Let\'s Talk Relief – Peace of Mind for IRS Situations',
      description: 'IRS letters and back taxes can be overwhelming. Our Let\'s Talk Relief tools and services are designed to bring order to the chaos.',
      features: [
        'IRS transcript reviews',
        'Clear summaries of what\'s actually going on',
        'Roadmaps and support options so you don\'t feel alone with the problem'
      ]
    },
    {
      title: 'Holiday & Seasonal Giveaways',
      description: 'From gift card drawings to special promotions, we run seasonal campaigns that give our community extra reasons to engage, learn, and feel supported.',
      features: [
        'Holiday forms with bonus resources',
        'Limited-time giveaways and drawings',
        'Opportunities to invite friends, family, and colleagues to join in'
      ]
    }
  ];

  const resources = [
    {
      title: 'Holiday Money-Saver Kit (PDF)',
      description: 'Practical, real-world tips to help you spend smarter and save more during the holidays.',
      cta: 'Get the Holiday Kit',
      link: '/book-consultation?source=community'
    },
    {
      title: 'First 10 Things to Do if You Get an IRS Letter',
      description: 'A simple one-page guide to help you respond calmly and correctly instead of reacting in panic.',
      cta: 'Download the IRS Letter Guide',
      link: '/book-consultation?source=community'
    },
    {
      title: 'New Business Launch Checklist',
      description: 'A clear list of the steps, decisions, and filings that matter most when you\'re starting a business.',
      cta: 'Get the Business Launch Checklist',
      link: '/book-consultation?source=community'
    },
    {
      title: 'Year-End Tax Prep Checklist',
      description: 'A straightforward checklist to help individuals and business owners feel more prepared before tax season hits.',
      cta: 'Get the Tax Prep Checklist',
      link: '/book-consultation?source=community'
    }
  ];

  const stories = [
    {
      quote: 'We went from "we\'re behind on everything" to "we\'ve got a plan."',
      story: 'A local family came to one of our events after receiving an IRS notice. They left with clear next steps, a realistic plan, and a lot less fear.'
    },
    {
      quote: 'I didn\'t think I was "big enough" for a CPA firm.',
      story: 'A solo business owner started with a networking breakfast, grabbed a free resource, and realized they didn\'t have to grow their business in the dark anymore.'
    },
    {
      quote: 'Our teenager walked away actually excited about money.',
      story: 'After attending an If You Teach Me event, one parent told us their teenager started asking questions about budgeting and investing in a way they never had before.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Community | KGOB CPAs & Advisors</title>
        <meta name="description" content="Join the KGOB community for events, resources, and financial education. You belong here whether you're a client, neighbor, or just tax-curious." />
        <link rel="canonical" href="https://kgob.com/community" />
        <meta property="og:title" content="Community | KGOB CPAs & Advisors" />
        <meta property="og:description" content="Join the KGOB community for events, resources, and financial education." />
        <meta property="og:url" content="https://kgob.com/community" />
      </Helmet>

      <SubscribeModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        listId="journal"
        source={subscribeSource}
      />

      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                You Belong in the KGOB Community
              </h1>
              
              <p className="text-xl text-[#D4AF37] mb-6">
                Whether you're a client, a neighbor, or just "tax-curious," you're welcome here. Our community is where money stress gets lighter, questions get answered, and you don't have to figure it all out alone.
              </p>
              
              <div className="space-y-4 text-lg text-[#F7F6F2]/90 mb-6">
                <p>
                  At KGOB, our firm works closely with a limited number of clients each year in a deep, advisory way. That's by design.
                </p>
                <p>
                  This community is how we widen that circle.
                </p>
                <p>
                  It's our front porch for people who are carrying a lot—business owners, families, and individuals facing real financial decisions—who want calm, credible guidance without feeling judged or rushed.
                </p>
                <p>
                  You don't have to be a current client or "good with numbers" to belong here. If you care about your money, your family, or your business, you're in the right place.
                </p>
                <p>
                  We're rooted in Charlotte, NC, but many of our workshops and resources are available to anyone in the U.S. who wants calmer, smarter financial decisions.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button
                  className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
                  onClick={() => openSubscribe('community_hero_newsletter')}
                  data-testid="community-hero-newsletter-btn"
                >
                  Join the Community Newsletter
                </Button>
                <Button data-testid="community-button-10" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="community-link-10" to="/book-consultation?source=community">See Upcoming Events</Link>
                </Button>
              </div>
              
              <p className="text-sm text-[#F7F6F2]/70 mt-4">
                No spam. Just real tips, local events, and occasional surprises.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section 2 - Who This Community Is For */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Who This Community Is For
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7] mb-8">
              <p className="text-[#4C5A70] mb-6">
                You don't need a certain income level, business size, or tax situation to belong here. Our community is designed for:
              </p>
              
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">🧑‍💼</span>
                  <p className="text-[#4C5A70]">
                    <strong>Business owners</strong> who want clean numbers, fewer surprises, and a trusted team to help them make smarter decisions
                  </p>
                </div>
                
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">👨‍👩‍👧</span>
                  <p className="text-[#4C5A70]">
                    <strong>Families</strong> who want less fear and more confidence around taxes, debt, and long-term planning
                  </p>
                </div>
                
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">🎓</span>
                  <p className="text-[#4C5A70]">
                    <strong>Students & young professionals</strong> who don't want to repeat the money mistakes they've seen
                  </p>
                </div>
                
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">🌎</span>
                  <p className="text-[#4C5A70]">
                    <strong>Locals and beyond</strong> — our events are based in North Carolina, but many of our tools and workshops are available online
                  </p>
                </div>
                
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">🤝</span>
                  <p className="text-[#4C5A70]">
                    <strong>Non-clients</strong> who are tired of worrying alone about IRS letters, messy books, or the feeling that they might be missing something important
                  </p>
                </div>
              </div>
              
              <p className="text-[#4C5A70] mt-6 font-medium">
                You do not have to be a client of KGOB to join our events, access many of our resources, or receive our community emails. If you're here, you're welcome.
              </p>
            </Card>
          </div>
        </section>

        {/* Section 3 - What You've Been Missing (Events) */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              What You've Been Missing (Until Now)
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              Money conversations don't have to happen in isolation or only once a year at tax time. We host live and virtual gatherings where you can learn, connect, and exhale a little.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {events.map((event, i) => {
                const Icon = event.icon;
                return (
                  <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#0B2240]" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{event.title}</h3>
                      </div>
                    </div>
                    <p className="text-[#4C5A70] text-sm">{event.description}</p>
                  </Card>
                );
              })}
            </div>
            
            <div className="text-center">
              <Button data-testid="community-button-9" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] mb-3">
                <Link data-testid="community-link-9" to="/book-consultation?source=community">View All Events & RSVP</Link>
              </Button>
              <p className="text-sm text-[#4C5A70]/70">
                Many events have limited seating or registration windows. Once they're full, they're full—make sure you're on the list.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 - Signature Community Initiatives */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              How We Give Back & Show Up
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              Our community work is not an afterthought. It's built into how we show up as a firm. Here are a few of the initiatives we're most proud of:
            </p>
            
            <div className="space-y-8">
              {initiatives.map((initiative, i) => (
                <Card key={i} className="p-8 border-2 border-[#E1DFD7]">
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">{initiative.title}</h3>
                  <p className="text-[#4C5A70] mb-4">{initiative.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    {(initiative.features || []).map((feature, j) => (
                      <div key={j} className="flex gap-3">
                        <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#4C5A70]">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {initiative.note && (
                    <p className="text-[#4C5A70] italic text-sm mt-4">{initiative.note}</p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5 - Choose Your Way to Plug In */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Choose Your Way to Plug In
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              You don't have to do everything. Just pick the first step that feels comfortable for you.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Stay in the Loop */}
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-8 h-8 text-[#D4AF37]" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">Stay in the Loop</h3>
                </div>
                <p className="text-[#4C5A70] mb-6">
                  Join the Community Newsletter for event invites, practical money tips, and updates on new tools and resources.
                </p>
                <Button
                  onClick={() => openSubscribe('community_newsletter_plugin')}
                  className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
                  data-testid="community-newsletter-plugin-btn"
                >
                  Join the Newsletter
                </Button>
              </Card>

              {/* Grab a Free Resource */}
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-4">
                  <Download className="w-8 h-8 text-[#D4AF37]" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">Grab a Free Resource</h3>
                </div>
                <p className="text-[#4C5A70] mb-4">
                  Start quietly, at your own pace.
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-[#4C5A70]">• Holiday Money-Saver Kit (PDF)</p>
                  <p className="text-sm text-[#4C5A70]">• "First 10 Things to Do if You Get an IRS Letter"</p>
                  <p className="text-sm text-[#4C5A70]">• New Business Launch Checklist</p>
                </div>
              </Card>

              {/* Come Say Hi In Person */}
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-8 h-8 text-[#D4AF37]" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">Come Say Hi In Person</h3>
                </div>
                <p className="text-[#4C5A70] mb-6">
                  If you're near one of our locations, we'd love to see you at a Business Networking Breakfast, Free Coffee Friday, or one of our special events.
                </p>
                <Button data-testid="community-button-8" asChild className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="community-link-8" to="/book-consultation?source=community">Find an Event Near You</Link>
                </Button>
              </Card>

              {/* Support If You Teach Me */}
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-4">
                  <Gift className="w-8 h-8 text-[#D4AF37]" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">Support If You Teach Me</h3>
                </div>
                <p className="text-[#4C5A70] mb-4">
                  Help us create more financial education moments for kids and families.
                </p>
                <div className="space-y-2 mb-6">
                  <p className="text-sm text-[#4C5A70]">• Donate toys or supplies</p>
                  <p className="text-sm text-[#4C5A70]">• Sponsor an event</p>
                  <p className="text-sm text-[#4C5A70]">• Volunteer your time</p>
                </div>
                <Button data-testid="community-button-7" asChild className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="community-link-7" to="/book-consultation?source=community">Learn How to Support IYTM</Link>
                </Button>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 6 - Local Business Spotlight */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Local Business Spotlight
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              We're grateful to serve and learn from remarkable business owners across the United States. Each month, we highlight one local business—not just what they do, but how they're growing, adapting, and leading in their space.
            </p>
            
            <Card className="p-8 border-2 border-[#E1DFD7] bg-gradient-to-br from-white to-[#F7F6F2]">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-8 h-8 text-[#D4AF37]" />
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">Featured Business</h3>
              </div>
              
              <p className="text-[#4C5A70] mb-4">
                When we first connected with the founder of a Winston-Salem family manufacturer, the business was growing thirty percent year over year but had no real visibility into margins by product line — and every April surprised them.
              </p>

              <p className="text-[#4C5A70] mb-4">
                Today, after rebuilding their chart of accounts, moving to monthly closes, and bringing entity structure in line with their growth, they make hiring and pricing decisions in days instead of quarters — and tax season is just paperwork.
              </p>

              <p className="text-[#4C5A70] italic">
                We're honored to be part of their advisory team and to support the financial side of the story they're building.
              </p>

              <p className="text-xs text-[#4C5A70]/60 mt-6 not-italic">
                Composite drawn from real client engagements. Identifying details changed to protect privacy.
              </p>
            </Card>

            <p className="text-sm text-[#4C5A70]/70 text-center mt-6">
              Interested in being considered for a future spotlight? Reach us at <a href="mailto:connect@kgob.com" className="underline hover:text-[#0B2240]" data-testid="community-spotlight-email">connect@kgob.com</a>.
            </p>
          </div>
        </section>

        {/* Section 7 - Real Stories, Real Relief */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Real People. Real Relief.
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              We protect privacy, but with permission (and sometimes changed details) we share stories that show what's possible when people stop trying to handle everything alone.
            </p>
            
            <div className="space-y-6">
              {stories.map((item, i) => (
                <Card key={i} className="p-8 border-2 border-[#E1DFD7]">
                  <p className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">
                    "{item.quote}"
                  </p>
                  <p className="text-[#4C5A70]">{item.story}</p>
                </Card>
              ))}
            </div>
            
            <p className="text-[#4C5A70] text-center mt-8 font-medium">
              You don't have to have it all together to show up here. In fact, that's kind of the point.
            </p>
          </div>
        </section>

        {/* Section 8 - Community Resource Library */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Community Resource Library
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-4 max-w-3xl mx-auto">
              These resources are here to help you breathe a little easier and make more informed decisions—whether you ever become a KGOB client or not.
            </p>
            
            <p className="text-[#4C5A70] text-center mb-12 max-w-3xl mx-auto">
              You might find:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {resources.map((resource, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex items-start gap-3 mb-4">
                    <Download className="w-6 h-6 text-[#D4AF37] mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{resource.title}</h3>
                      <p className="text-[#4C5A70] text-sm mb-4">{resource.description}</p>
                      <Button data-testid="community-button-6" asChild size="sm" className="bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                        <Link data-testid="community-link-6" to={resource.link}>{resource.cta}</Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            <div className="text-center mb-8">
              <Button data-testid="community-button-5" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="community-link-5" to="/book-consultation?source=community">Browse Community Resources</Link>
              </Button>
            </div>
            
            <p className="text-xs text-[#4C5A70]/70 text-center max-w-3xl mx-auto">
              These resources are for general education and planning only and are not a substitute for personalized tax, legal, or accounting advice. When you're ready for tailored guidance, our team is here to help you evaluate next steps.
            </p>
          </div>
        </section>

        {/* Section 9 - For Schools, Nonprofits & Community Groups */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              For Schools, Nonprofits & Community Groups
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7] mb-8">
              <p className="text-[#4C5A70] mb-6">
                If you serve people who would benefit from better financial understanding, we'd love to talk about partnering.
              </p>
              
              <p className="text-[#4C5A70] mb-4 font-medium">We can:</p>
              <div className="space-y-2 mb-6">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">Speak at workshops, lunch-and-learns, or parent nights</span>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">Host Q&A sessions about taxes, budgeting, business, or financial literacy</span>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">Collaborate on events that combine education with practical support</span>
                </div>
              </div>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg mb-6">
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-4">Three-Step Flow</h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-sm">1</span>
                    </div>
                    <p className="text-[#4C5A70]">Submit a brief request</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-sm">2</span>
                    </div>
                    <p className="text-[#4C5A70]">We review fit and availability</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-sm">3</span>
                    </div>
                    <p className="text-[#4C5A70]">Together, we design something that serves your audience well</p>
                  </div>
                </div>
              </div>
              
              <Button data-testid="community-button-4" asChild className="w-full h-14 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="community-link-4" to="/book-consultation?source=community">Request a Community Speaker or Partner</Link>
              </Button>
            </Card>
          </div>
        </section>

        {/* Section 10 - Community Newsletter Feature */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Get the Inside Track (Without the Noise)
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                Our Community Newsletter is the easiest way to stay connected without adding more to your to-do list.
              </p>
              
              <p className="text-[#4C5A70] mb-4 font-medium">You'll receive:</p>
              <div className="space-y-2 mb-6">
                {[
                  'First notice of upcoming events and workshops',
                  'Priority access to limited-seating sessions',
                  'Updates on new tools and resources across the KGOB and Let\'s Talk family',
                  'Occasional giveaways, community highlights, and behind-the-scenes updates',
                  'Short, plain-English guidance on changes in the tax and financial landscape'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-[#4C5A70] mb-8">
                We typically email 1–2 times a month. No jargon. No clutter. Just clear, practical updates you can actually use.
              </p>
              
              <Button data-testid="community-button-3" asChild className="w-full h-14 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="community-link-3" to="/book-consultation?source=community">Yes, keep me in the loop</Link>
              </Button>
            </Card>
          </div>
        </section>

        {/* Section 11 - Closing Invitation */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              You Don't Have to Do Money Alone
            </h2>
            
            <div className="space-y-4 text-lg text-[#F7F6F2]/90 mb-10">
              <p>
                Taxes, cash flow, debt, growth—it's a lot. You deserve a community that meets you with empathy, clarity, and real help.
              </p>
              <p>
                Our firm works deeply with a limited number of clients each year.
              </p>
              <p>
                Our community, however, is intentionally open to anyone who wants: Less anxiety around money • More clarity and control • A place where honest financial conversations are normal
              </p>
              <p>
                Every event, resource, and tool we create is designed to give you more clarity, more confidence, and a better path forward.
              </p>
              <p>
                Whether you're just meeting us for the first time or you've known KGOB for years, you're invited.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button
                onClick={() => openSubscribe('community_close_newsletter')}
                className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
                data-testid="community-close-newsletter-btn"
              >
                Join the Newsletter
              </Button>
              <Button data-testid="community-button-2" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="community-link-2" to="/book-consultation?source=community">Find an Event</Link>
              </Button>
              <Button data-testid="community-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="community-link-1" to="/book-consultation?source=community">Grab a Free Resource</Link>
              </Button>
            </div>
            
            <p className="text-[#F7F6F2]/80">
              We'd love for you to be part of the KGOB community—in whatever way feels right for you right now.
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Community;